import React, { useState, useEffect, useRef } from 'react';
import { Zap, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import api from '../../services/api';
import { displayCategory } from '../../utils';
import BoostBadge from './BoostBadge';

// Spotlight-Hero ueber Trending: praesentiert geboostete Markets
// prominent im zweispaltigen Layout (Kennzahlen links, Verlaufschart
// rechts). Bei mehreren Boosts per Dots durchblaetterbar. Rendert
// nichts, wenn keine aktiven Boosts. Da unsere Maerkte binaer sind
// (JA/NEIN), zeigt die linke Spalte die beiden Seiten statt eines
// Kandidaten-Rankings; der Chart nutzt die YES-Verlaufsserie aus
// /price-history.
const BoostSpotlight = ({ markets, outcomes, navigate }) => {
  const [idx, setIdx] = useState(0);
  const [history, setHistory] = useState(null);
  const [paused, setPaused] = useState(false);
  const hasMarkets = markets && markets.length > 0;
  const count = hasMarkets ? markets.length : 0;
  const safeIdx = hasMarkets ? idx % count : 0;
  const market = hasMarkets ? markets[safeIdx] : null;
  const marketId = market?.id;

  const goTo = (i) => setIdx(((i % count) + count) % count);
  const goPrev = () => goTo(safeIdx - 1);
  const goNext = () => goTo(safeIdx + 1);

  // Touch-Swipe (Mobile): horizontale Wischgeste blaettert wie die
  // Pfeile/Dots. Nur bei mehreren Boosts; klare horizontale Wische
  // (mind. 40px, horizontal dominiert vertikal) damit vertikales
  // Scrollen nicht versehentlich blaettert.
  const touchStartRef = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (count <= 1 || !touchStartRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) goNext();  // nach links -> naechster
    else goPrev();         // nach rechts -> vorheriger
  };

  useEffect(() => {
    if (!marketId) { setHistory(null); return; }
    let cancelled = false;
    setHistory(null);
    api.markets.getPriceHistory(marketId, { days: 30 })
      .then(res => { if (!cancelled) setHistory(res.data); })
      .catch(() => { if (!cancelled) setHistory(null); });
    return () => { cancelled = true; };
  }, [marketId]);

  // Auto-Rotation alle 7s bei mehreren Boosts; pausiert bei Hover.
  useEffect(() => {
    if (count <= 1 || paused) return;
    const t = setInterval(() => setIdx(i => (i + 1) % count), 7000);
    return () => clearInterval(t);
  }, [count, paused]);

  if (!hasMarkets) return null;

  const primary = (outcomes[market.id] || [])[0];
  const yesPct = primary ? Math.round((primary.price ?? 0) * 100) : null;
  const noPct = yesPct != null ? 100 - yesPct : null;
  // Echtes Wett-Volumen (placed-bet VC) aus dem /markets-JSON, nicht
  // die Pool-Summe (die Seed-Liquiditaet enthaelt).
  const volume = Math.round(market.volume || 0);
  const endLabel = market.closes_at
    ? new Date(market.closes_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const yesSeries = history?.YES || [];

  const goToMarket = () => navigate(`/markets/${market.id}`);

  return (
    <div className="mb-8" data-testid="boost-spotlight">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-5 w-5 text-orange-500" fill="currentColor" />
        <h3 className="text-lg font-bold bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent">Im Rampenlicht</h3>
      </div>
      <div
        className="overflow-hidden rounded-2xl border border-orange-200 dark:border-orange-500/30 bg-white dark:bg-slate-900/80 dark:backdrop-blur-sm shadow-lg shadow-orange-500/10"
        data-testid={`boost-spotlight-card-${market.id}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Header: Icon/Cover + Kategorie + Titel + Boosted-Badge */}
        <div className="flex items-start gap-3 p-5 sm:p-6 pb-3">
          {market.image_url ? (
            <img
              src={market.image_url}
              alt=""
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-gray-200 dark:border-slate-700"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-12 h-12 rounded-lg flex-shrink-0 bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
              <Zap className="h-6 w-6 text-white" fill="currentColor" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{displayCategory(market.category)}</div>
            <button type="button" onClick={goToMarket} className="text-left block w-full">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                {market.title}
              </h4>
            </button>
          </div>
          <BoostBadge size="md" className="flex-shrink-0" />
        </div>

        {/* Zweispaltig: Kennzahlen links, Chart rechts */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 sm:gap-6 px-5 sm:px-6 pb-5">
          {/* Links: JA/NEIN + Kennzahlen + CTA */}
          <div className="lg:col-span-2 space-y-4">
            {yesPct != null && (
              <div className="space-y-3">
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">JA</span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{yesPct}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${yesPct}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">NEIN</span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{noPct}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800">
                    <div className="h-full rounded-full bg-gradient-to-r from-rose-400 to-rose-600" style={{ width: `${noPct}%` }} />
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-400 dark:text-gray-500">Volumen</div>
                <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  {volume != null ? `${volume.toLocaleString('de-DE')} VC` : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 dark:text-gray-500">Endet</div>
                <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{endLabel || '—'}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={goToMarket}
              className="w-full inline-flex items-center justify-center gap-1 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 transition-colors"
            >
              Jetzt wetten <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Rechts: Verlaufschart (YES) aus /price-history */}
          <div className="lg:col-span-3 min-h-[200px]">
            {yesSeries.length > 1 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={yesSeries} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="boostYesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} className="dark:opacity-20" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} minTickGap={28} />
                  <YAxis domain={[0, 1]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} width={36} />
                  <Tooltip formatter={(v) => [`${(v * 100).toFixed(1)}%`, 'JA']} labelFormatter={(l) => l} />
                  <Area type="monotone" dataKey="close" stroke="#10b981" fill="url(#boostYesGradient)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex flex-col items-center justify-center gap-1 rounded-lg bg-gray-50 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400">
                {yesSeries.length === 1 && yesPct != null ? (
                  <>
                    <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{yesPct}%</div>
                    <div className="text-xs">noch kein Verlauf</div>
                  </>
                ) : (
                  <div className="text-sm">Noch keine Verlaufsdaten</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-3 border-t border-gray-100 dark:border-slate-800 text-xs text-gray-500 dark:text-gray-400">
          <span>{volume != null ? `${volume.toLocaleString('de-DE')} VC Vol.` : ''}</span>
          <span>{endLabel ? `Endet ${endLabel}` : ''}</span>
        </div>
      </div>

      {count > 1 && (
        <div className="flex items-center justify-center gap-3 mt-3">
          <button
            type="button"
            onClick={goPrev}
            aria-label="Vorheriger Boost"
            className="spotlight-nav inline-flex items-center justify-center h-7 w-7 rounded-full border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-orange-300 hover:text-orange-500 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            {markets.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Boost ${i + 1} von ${count}`}
                className={`spotlight-nav h-2.5 rounded-full transition-all ${i === safeIdx ? 'w-6 bg-orange-500' : 'w-2.5 bg-gray-300 dark:bg-slate-700 hover:bg-gray-400'}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={goNext}
            aria-label="Nächster Boost"
            className="spotlight-nav inline-flex items-center justify-center h-7 w-7 rounded-full border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-orange-300 hover:text-orange-500 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default BoostSpotlight;
